import { PendingCardToken, ReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';

import { getPendingCardTokensForDevice } from '@domain/digital-wallet/utils/pending-cards-mapper';
import {
    ActivationCardToken,
    ApplePayEligibleCard,
    CreditCardToken,
    DeviceType,
    WalletCreditCard,
} from '@generated-types';

import { AppleWalletService } from '../services';
import { ApplePayCardNativeState, ApplePayCardNativeStateKb } from '../types';

export const getApplePayCardNativeState = <T extends ActivationCardToken>(
    selectedEligibleCard: ApplePayEligibleCard,
    nativeProps: ReactNativeProps,
    pendingTokensForCard: T[],
): ApplePayCardNativeState => {
    const cardIsInRemoteWallet = selectedEligibleCard.tokens.some(
        (token) =>
            token.deviceType === DeviceType.WATCH &&
            (nativeProps.appleWallet?.remotePasses ?? []).some(
                (remotePass) => remotePass.value === token.panUniqueReference,
            ),
    );

    const cardIsInLocalWallet = selectedEligibleCard.tokens.some(
        (token) =>
            token.deviceType !== DeviceType.WATCH &&
            (nativeProps.appleWallet?.localPasses ?? []).some(
                (localPass) => localPass.value === token.panUniqueReference,
            ),
    );

    const pendingDeviceTokens = getPendingCardTokensForDevice(
        pendingTokensForCard,
        nativeProps.appleWallet?.pendingCardTokens,
    );

    return {
        cardIsInLocalWallet,
        cardIsInRemoteWallet,
        pendingDeviceTokens,
    };
};

export const getPendingCardTokensForDeviceKb = (
    pendingCardTokens: CreditCardToken[],
    nativeTokens: PendingCardToken[] = [],
): CreditCardToken[] =>
    pendingCardTokens.filter((pendingCard) =>
        nativeTokens.some(
            (nativeToken) =>
                nativeToken.primaryAccountIdentifier.value === pendingCard.panReferenceId &&
                nativeToken.tokenUniqueReference.value === pendingCard.uniqueReferenceId,
        ),
    );

export const getApplePayCardNativeStateKb = (
    walletCard: WalletCreditCard,
    nativeProps: ReactNativeProps,
): ApplePayCardNativeStateKb => {
    const pendingTokens = AppleWalletService.getPendingTokens(walletCard);
    const activeTokens = AppleWalletService.getActiveTokens(walletCard);

    const addedTokens = [...pendingTokens, ...activeTokens];

    const cardIsInRemoteWallet = addedTokens.some(
        (token) =>
            token.device.type === 'WATCH' &&
            (nativeProps.appleWallet?.remotePasses ?? []).some(
                (remotePass) => remotePass.value === token.panReferenceId,
            ),
    );

    const cardIsInLocalWallet = addedTokens.some(
        (token) =>
            token.device.type !== 'WATCH' &&
            (nativeProps.appleWallet?.localPasses ?? []).some((localPass) => localPass.value === token.panReferenceId),
    );

    const pendingDeviceTokens = getPendingCardTokensForDeviceKb(
        pendingTokens,
        nativeProps.appleWallet?.pendingCardTokens,
    );

    return {
        cardIsInLocalWallet,
        cardIsInRemoteWallet,
        pendingDeviceTokens,
    };
};
